import { useState, useEffect } from 'react'
import { channelsApi, type Channel, type ChannelPost } from '../../api/channels'
import { usersApi } from '../../api/users'
import type { User } from '../../types'
import { analyticsApi, type ChannelDashboard, type SubscriberGrowth, type DemographicStats, type PostStats } from '../../api/analytics'
import { mediaApi } from '../../api/media'
import { useAuthStore } from '../../stores/authStore'
import { useUiStore } from '../../stores/uiStore'
import { Avatar } from '../ui/Avatar'
import { Spinner } from '../ui/Spinner'
import { parseFormatting } from '../../utils/formatText'

interface Props {
  channelId: string
  onClose: () => void
}

function PostCard({ post: initialPost, channelId, isOwner, onDelete, onUpdate }: {
  post: ChannelPost
  channelId: string
  isOwner: boolean
  onDelete: (id: string) => void
  onUpdate: (post: ChannelPost) => void
}) {
  const { openMedia, addToast } = useUiStore()
  const [post, setPost] = useState(initialPost)
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState(post.text)
  const [saving, setSaving] = useState(false)

  const handleReact = async (emoji: string) => {
    const r = post.reactions?.find((x) => x.emoji === emoji)
    try {
      if (r?.my_react) {
        await channelsApi.unreact(channelId, post.id)
      } else {
        await channelsApi.react(channelId, post.id, emoji)
      }
    } catch {
      addToast('error', 'Не удалось поставить реакцию')
    }
  }

  const handlePin = async () => {
    try {
      await channelsApi.pinPost(channelId, post.id)
      const updated = { ...post, is_pinned: !post.is_pinned }
      setPost(updated)
      onUpdate(updated)
      addToast('success', post.is_pinned ? 'Пост откреплён' : 'Пост закреплён')
    } catch {
      addToast('error', 'Не удалось закрепить пост')
    }
  }

  const handleEdit = async () => {
    if (!editText.trim()) return
    setSaving(true)
    try {
      const updated = await channelsApi.editPost(channelId, post.id, { text: editText.trim() })
      setPost(updated)
      onUpdate(updated)
      setEditing(false)
    } catch {
      addToast('error', 'Не удалось изменить пост')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={`bg-bg-primary border rounded-2xl overflow-hidden mb-3 ${post.is_pinned ? 'border-accent/30' : 'border-divider'}`}>
      {post.is_pinned && (
        <div className="flex items-center gap-1.5 px-4 py-1.5 bg-accent/10 border-b border-accent/20">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="text-accent">
            <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
          </svg>
          <span className="text-accent text-xs">Pinned post</span>
        </div>
      )}
      {/* Media */}
      {post.media_ids?.length && (
        <div className={`grid gap-0.5 ${post.media_ids.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {post.media_ids.slice(0, 4).map((id) => (
            <button key={id} onClick={() => openMedia(mediaApi.getURL(id))} className="relative">
              <img
                src={mediaApi.getThumbnailURL(id, '800x800')}
                alt="post media"
                className="w-full object-cover max-h-64"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      <div className="px-4 py-3">
        {/* Text / edit */}
        {editing ? (
          <div className="mb-2 space-y-2">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={3}
              autoFocus
              className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent resize-none"
            />
            <div className="flex gap-2">
              <button onClick={() => setEditing(false)} className="flex-1 text-txt-secondary bg-bg-input py-1.5 rounded-lg text-sm hover:bg-bg-hover">Cancel</button>
              <button onClick={handleEdit} disabled={saving} className="flex-1 bg-accent text-white py-1.5 rounded-lg text-sm disabled:opacity-40 hover:bg-blue-500">
                {saving ? '…' : 'Save'}
              </button>
            </div>
          </div>
        ) : post.text ? (
          <p className="text-txt-primary text-[15px] leading-relaxed whitespace-pre-wrap break-words mb-2">
            {parseFormatting(post.text)}
          </p>
        ) : null}

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {['👍', '❤️', '🔥', '😮'].map((emoji) => {
              const r = post.reactions?.find((x) => x.emoji === emoji)
              const reacted = r?.my_react
              return (
                <button
                  key={emoji}
                  onClick={() => handleReact(emoji)}
                  className={`text-sm px-2 py-0.5 rounded-full transition-colors
                    ${reacted ? 'bg-accent/20 text-accent' : 'hover:bg-bg-hover text-txt-secondary'}`}
                >
                  {emoji}{r && r.count > 0 ? ` ${r.count}` : ''}
                </button>
              )
            })}
          </div>
          <div className="flex items-center gap-3 text-txt-meta text-xs">
            <span>👁 {post.views_count}</span>
            <span>{new Date(post.created_at).toLocaleDateString()}</span>
            {isOwner && (
              <>
                <button onClick={handlePin} title={post.is_pinned ? 'Unpin' : 'Pin'} className="hover:text-accent transition-colors">
                  📌
                </button>
                <button onClick={() => { setEditing(true); setEditText(post.text) }} className="hover:text-txt-primary transition-colors">Edit</button>
                <button onClick={() => onDelete(post.id)} className="text-red-400 hover:text-red-300 transition-colors">Delete</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function ChannelView({ channelId, onClose }: Props) {
  const [channel, setChannel] = useState<Channel | null>(null)
  const [posts, setPosts] = useState<ChannelPost[]>([])
  const [loading, setLoading] = useState(true)
  const [subscribed, setSubscribed] = useState(false)
  const [newPost, setNewPost] = useState('')
  const [posting, setPosting] = useState(false)
  const [tab, setTab] = useState<'posts' | 'stats' | 'settings' | 'subscribers'>('posts')
  const [stats, setStats] = useState<ChannelDashboard | null>(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const [growth, setGrowth] = useState<SubscriberGrowth[]>([])
  const [demographics, setDemographics] = useState<DemographicStats | null>(null)
  const [postStats, setPostStats] = useState<PostStats[]>([])
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [editPublic, setEditPublic] = useState(false)
  const [editComments, setEditComments] = useState(false)
  const [savingSettings, setSavingSettings] = useState(false)
  const [subscriberList, setSubscriberList] = useState<Array<{ user_id: string; role: string; profile?: User }>>([])
  const [subsLoading, setSubsLoading] = useState(false)
  const { userId } = useAuthStore()
  const { addToast } = useUiStore()

  useEffect(() => {
    Promise.all([channelsApi.get(channelId), channelsApi.getPosts(channelId)])
      .then(([ch, p]) => {
        setChannel(ch)
        setPosts((p.posts ?? []).reverse())
        setEditName(ch.name)
        setEditDesc(ch.description ?? '')
        setEditPublic(ch.is_public ?? false)
        setEditComments(ch.allow_comments ?? false)
      })
      .catch(() => addToast('error', 'Не удалось загрузить канал'))
      .finally(() => setLoading(false))

    channelsApi.getSubscribers(channelId)
      .then((r) => setSubscribed(r.subscribers?.some((s: { user_id: string }) => s.user_id === userId) ?? false))
      .catch(() => {})
  }, [channelId, userId, addToast])

  const handleSubscribe = async () => {
    try {
      if (subscribed) {
        await channelsApi.unsubscribe(channelId)
        setSubscribed(false)
      } else {
        await channelsApi.subscribe(channelId)
        setSubscribed(true)
      }
    } catch {
      addToast('error', 'Не удалось обновить подписку')
    }
  }

  const handlePost = async () => {
    if (!newPost.trim() || posting) return
    setPosting(true)
    try {
      const post = await channelsApi.createPost(channelId, { text: newPost.trim() })
      setPosts((p) => [...p, post])
      setNewPost('')
    } catch {
      addToast('error', 'Не удалось опубликовать пост')
    } finally {
      setPosting(false)
    }
  }

  const handleSaveSettings = async () => {
    setSavingSettings(true)
    try {
      const updated = await channelsApi.update(channelId, {
        name: editName.trim() || undefined,
        description: editDesc.trim() || undefined,
        is_public: editPublic,
        allow_comments: editComments,
      })
      setChannel(updated)
      addToast('success', 'Настройки канала сохранены')
    } catch {
      addToast('error', 'Не удалось сохранить настройки')
    } finally {
      setSavingSettings(false)
    }
  }

  const handleDelete = async (postId: string) => {
    try {
      await channelsApi.deletePost(channelId, postId)
      setPosts((p) => p.filter((x) => x.id !== postId))
    } catch {
      addToast('error', 'Не удалось удалить пост')
    }
  }

  const isOwner = channel?.owner_id === userId

  const loadSubscribers = async () => {
    if (subscriberList.length > 0 || subsLoading) return
    setSubsLoading(true)
    try {
      const { subscribers } = await channelsApi.getSubscribers(channelId)
      const list = subscribers ?? []
      setSubscriberList(list.map((s) => ({ user_id: s.user_id, role: s.role })))
      // Load profiles for first 30
      const profiles = await Promise.allSettled(list.slice(0, 30).map((s) => usersApi.getProfile(s.user_id)))
      setSubscriberList(list.map((s, i) => ({
        user_id: s.user_id,
        role: s.role,
        profile: profiles[i]?.status === 'fulfilled' ? (profiles[i] as PromiseFulfilledResult<User>).value : undefined,
      })))
    } catch { /* ignore */ }
    finally { setSubsLoading(false) }
  }

  const loadStats = () => {
    if (stats || statsLoading) return
    setStatsLoading(true)
    Promise.all([
      analyticsApi.channelDashboard(channelId),
      analyticsApi.subscriberGrowth(channelId),
      analyticsApi.postStats(channelId).catch(() => ({ posts: [] })),
      analyticsApi.demographics(channelId).catch(() => null),
    ])
      .then(([dash, growthData, postData, demoData]) => {
        setStats(dash)
        setGrowth(growthData.growth ?? [])
        setPostStats(postData.posts ?? [])
        setDemographics(demoData)
      })
      .catch(() => addToast('error', 'Не удалось загрузить статистику'))
      .finally(() => setStatsLoading(false))
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-8 px-4"
         onClick={onClose}>
      <div className="w-full max-w-2xl animate-fade-in" onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div className="flex justify-center py-16"><Spinner size={32} /></div>
        ) : channel ? (
          <>
            {/* Header */}
            <div className="bg-bg-primary rounded-2xl p-4 mb-4 border border-divider flex items-center gap-4">
              <Avatar name={channel.name} src={channel.avatar_file_id ? undefined : undefined} size={56} />
              <div className="flex-1 min-w-0">
                <p className="text-txt-primary font-bold text-lg truncate">{channel.name}</p>
                {channel.username && <p className="text-txt-meta text-sm">@{channel.username}</p>}
                {channel.description && <p className="text-txt-secondary text-sm mt-0.5">{channel.description}</p>}
                <p className="text-txt-meta text-xs mt-1">{channel.subscriber_count} subscribers</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={handleSubscribe}
                  className={`px-4 py-1.5 rounded-xl text-sm font-medium transition-colors
                    ${subscribed ? 'bg-bg-input text-txt-secondary hover:bg-bg-hover' : 'bg-accent text-white hover:bg-blue-500'}`}
                >
                  {subscribed ? 'Subscribed' : 'Subscribe'}
                </button>
                <button onClick={onClose} className="text-txt-meta hover:text-txt-primary text-lg px-2">✕</button>
              </div>
            </div>

            {/* Tabs (owner only) */}
            {isOwner && (
              <div className="flex gap-1 mb-3 bg-bg-primary rounded-xl p-1 border border-divider">
                {(['posts', 'subscribers', 'stats', 'settings'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => { setTab(t); if (t === 'stats') loadStats(); if (t === 'subscribers') loadSubscribers() }}
                    className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors
                      ${tab === t ? 'bg-accent text-white' : 'text-txt-secondary hover:text-txt-primary hover:bg-bg-hover'}`}
                  >
                    {t === 'posts' ? 'Posts' : t === 'subscribers' ? 'Subscribers' : t === 'stats' ? 'Stats' : 'Settings'}
                  </button>
                ))}
              </div>
            )}

            {/* Stats panel */}
            {tab === 'stats' && isOwner && (
              <div className="bg-bg-primary rounded-2xl p-4 border border-divider mb-4">
                {statsLoading ? (
                  <div className="flex justify-center py-8"><Spinner /></div>
                ) : stats ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-txt-secondary text-xs uppercase tracking-wider font-medium">Last 30 days</p>
                      <a
                        href={analyticsApi.exportCSVUrl(channelId)}
                        download="channel_stats.csv"
                        className="text-accent text-xs hover:underline flex items-center gap-1"
                      >
                        ⬇ Export CSV
                      </a>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: 'Subscribers', value: stats.total_subs.toLocaleString() },
                        { label: 'Total Views', value: stats.total_views.toLocaleString() },
                        { label: 'Avg Views/Post', value: Math.round(stats.avg_views_per_post).toLocaleString() },
                      ].map((s) => (
                        <div key={s.label} className="bg-bg-input rounded-xl p-3 text-center">
                          <p className="text-txt-primary font-bold text-xl">{s.value}</p>
                          <p className="text-txt-meta text-xs mt-0.5">{s.label}</p>
                        </div>
                      ))}
                    </div>
                    {stats.daily_series?.length > 0 && (
                      <div>
                        <p className="text-txt-secondary text-xs mb-2 uppercase tracking-wider">Daily views</p>
                        <div className="flex items-end gap-0.5 h-16">
                          {stats.daily_series.slice(-30).map((d, i) => {
                            const maxViews = Math.max(...stats.daily_series.map((x) => x.views ?? 0), 1)
                            const h = Math.max(4, ((d.views ?? 0) / maxViews) * 60)
                            return (
                              <div key={i} className="flex-1 bg-accent/60 rounded-sm" style={{ height: h }} title={`${d.date}: ${d.views} views`} />
                            )
                          })}
                        </div>
                      </div>
                    )}
                    {growth.length > 0 && (
                      <div>
                        <p className="text-txt-secondary text-xs mb-2 uppercase tracking-wider">Subscriber growth</p>
                        <div className="flex items-end gap-0.5 h-12">
                          {growth.slice(-30).map((g, i) => {
                            const maxDelta = Math.max(...growth.map((x) => Math.abs(x.delta ?? 0)), 1)
                            const h = Math.max(4, (Math.abs(g.delta ?? 0) / maxDelta) * 44)
                            const isPos = (g.delta ?? 0) >= 0
                            return (
                              <div
                                key={i}
                                className={`flex-1 rounded-sm ${isPos ? 'bg-green-400/60' : 'bg-red-400/60'}`}
                                style={{ height: h }}
                                title={`${g.date}: ${isPos ? '+' : ''}${g.delta} subs`}
                              />
                            )
                          })}
                        </div>
                      </div>
                    )}
                    {/* Top posts by views */}
                    {postStats.length > 0 && (
                      <div>
                        <p className="text-txt-secondary text-xs mb-2 uppercase tracking-wider">Top posts by views</p>
                        <div className="space-y-1">
                          {postStats.slice(0, 5).map((p) => (
                            <div key={p.post_id} className="flex items-center gap-2">
                              <p className="text-txt-meta text-xs font-mono w-20 shrink-0 truncate">#{p.post_id.slice(-6)}</p>
                              <div className="flex-1 h-2 rounded-full bg-bg-hover overflow-hidden">
                                <div
                                  className="h-full bg-accent/60 rounded-full"
                                  style={{ width: `${Math.min(100, (p.views / Math.max(...postStats.map((x) => x.views), 1)) * 100)}%` }}
                                />
                              </div>
                              <p className="text-txt-primary text-xs w-12 text-right shrink-0">{p.views.toLocaleString()}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Demographics */}
                    {demographics && Object.keys(demographics.countries ?? {}).length > 0 && (
                      <div>
                        <p className="text-txt-secondary text-xs mb-2 uppercase tracking-wider">Top countries</p>
                        <div className="space-y-1">
                          {Object.entries(demographics.countries)
                            .sort(([, a], [, b]) => b - a)
                            .slice(0, 5)
                            .map(([country, count]) => {
                              const total = Object.values(demographics.countries).reduce((a, b) => a + b, 0) || 1
                              return (
                                <div key={country} className="flex items-center gap-2">
                                  <p className="text-txt-meta text-xs w-8 shrink-0 uppercase">{country}</p>
                                  <div className="flex-1 h-2 rounded-full bg-bg-hover overflow-hidden">
                                    <div
                                      className="h-full bg-green-400/60 rounded-full"
                                      style={{ width: `${(count / total) * 100}%` }}
                                    />
                                  </div>
                                  <p className="text-txt-primary text-xs w-8 text-right shrink-0">{Math.round((count / total) * 100)}%</p>
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    )}
                    {demographics && Object.keys(demographics.platforms ?? {}).length > 0 && (
                      <div>
                        <p className="text-txt-secondary text-xs mb-2 uppercase tracking-wider">Platforms</p>
                        <div className="flex gap-2 flex-wrap">
                          {Object.entries(demographics.platforms)
                            .sort(([, a], [, b]) => b - a)
                            .map(([platform, count]) => {
                              const total = Object.values(demographics.platforms).reduce((a, b) => a + b, 0) || 1
                              return (
                                <div key={platform} className="bg-bg-input rounded-xl px-3 py-1.5 text-center">
                                  <p className="text-txt-primary text-sm font-medium capitalize">{platform}</p>
                                  <p className="text-txt-meta text-xs">{Math.round((count / total) * 100)}%</p>
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-txt-meta text-sm text-center py-4">No data yet</p>
                )}
              </div>
            )}

            {/* Subscribers tab (owner only) */}
            {tab === 'subscribers' && isOwner && (
              <div className="bg-bg-primary rounded-2xl p-4 border border-divider mb-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-txt-secondary text-xs uppercase tracking-wider font-medium">
                    {channel.subscriber_count.toLocaleString()} subscribers
                  </p>
                </div>
                {subsLoading ? (
                  <div className="flex justify-center py-8"><Spinner /></div>
                ) : subscriberList.length === 0 ? (
                  <p className="text-txt-meta text-sm text-center py-4">No subscribers yet</p>
                ) : (
                  <div className="space-y-2">
                    {subscriberList.map((sub) => {
                      const name = sub.profile
                        ? [sub.profile.first_name, sub.profile.last_name].filter(Boolean).join(' ') || sub.profile.username || sub.user_id
                        : sub.user_id.slice(0, 8) + '…'
                      return (
                        <div key={sub.user_id} className="flex items-center gap-3 py-1">
                          <Avatar name={name} src={sub.profile?.avatar_url ? undefined : undefined} size={36} />
                          <div className="flex-1 min-w-0">
                            <p className="text-txt-primary text-sm truncate">{name}</p>
                            {sub.profile?.username && (
                              <p className="text-txt-meta text-xs">@{sub.profile.username}</p>
                            )}
                          </div>
                          {sub.role !== 'subscriber' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-accent/15 text-accent font-medium capitalize">
                              {sub.role}
                            </span>
                          )}
                        </div>
                      )
                    })}
                    {subscriberList.length < channel.subscriber_count && (
                      <p className="text-txt-meta text-xs text-center pt-2">
                        Showing {subscriberList.length} of {channel.subscriber_count.toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Settings tab (owner only) */}
            {tab === 'settings' && isOwner && (
              <div className="bg-bg-primary rounded-2xl p-4 border border-divider mb-4 space-y-4">
                <div>
                  <label className="text-txt-secondary text-xs uppercase tracking-wider font-medium block mb-1.5">Channel name</label>
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent"
                  />
                </div>
                <div>
                  <label className="text-txt-secondary text-xs uppercase tracking-wider font-medium block mb-1.5">Description</label>
                  <textarea
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    rows={3}
                    className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent resize-none"
                  />
                </div>
                <div className="flex items-center justify-between py-1">
                  <div>
                    <p className="text-txt-primary text-sm">Public channel</p>
                    <p className="text-txt-meta text-xs">Visible in search and joinable by link</p>
                  </div>
                  <button
                    onClick={() => setEditPublic((v) => !v)}
                    className={`w-11 h-6 rounded-full transition-colors relative ${editPublic ? 'bg-accent' : 'bg-txt-meta/30'}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${editPublic ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                <div className="flex items-center justify-between py-1">
                  <div>
                    <p className="text-txt-primary text-sm">Allow comments</p>
                    <p className="text-txt-meta text-xs">Readers can react and discuss posts</p>
                  </div>
                  <button
                    onClick={() => setEditComments((v) => !v)}
                    className={`w-11 h-6 rounded-full transition-colors relative ${editComments ? 'bg-accent' : 'bg-txt-meta/30'}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${editComments ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                <button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="w-full bg-accent text-white py-2.5 rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-blue-500 transition-colors"
                >
                  {savingSettings ? 'Saving…' : 'Save settings'}
                </button>
              </div>
            )}

            {/* Posts */}
            {tab === 'posts' && posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                channelId={channelId}
                isOwner={isOwner}
                onDelete={handleDelete}
                onUpdate={(updated) => setPosts((prev) => prev.map((p) => p.id === updated.id ? updated : p))}
              />
            ))}

            {/* Publish (owner only, posts tab) */}
            {isOwner && tab === 'posts' && (
              <div className="bg-bg-primary rounded-2xl p-4 border border-divider mt-2">
                <textarea
                  value={newPost}
                  onChange={(e) => setNewPost(e.target.value)}
                  placeholder="Написать пост..."
                  rows={3}
                  className="w-full bg-bg-input text-txt-primary rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent resize-none mb-2"
                />
                <button
                  onClick={handlePost}
                  disabled={!newPost.trim() || posting}
                  className="bg-accent text-white px-4 py-1.5 rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-blue-500 transition-colors"
                >
                  {posting ? 'Publishing...' : 'Publish'}
                </button>
              </div>
            )}

            {tab === 'posts' && posts.length === 0 && !isOwner && (
              <div className="text-center py-8 text-txt-meta text-sm">No posts yet</div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-txt-meta">Channel not found</div>
        )}
      </div>
    </div>
  )
}
